import { useState, useEffect, useRef } from "react";
import { Modal, Button, Select } from "@legal-500/legal500-design-system";
import { templates as templatesApi } from "@/api/templates.service";
import styles from "./MailingGroups.module.css";

type AddReminderModalProps = {
  isOpen: boolean;
  // Whether an invitation has already been sent to this group. `false` triggers a
  // warning (a reminder would currently target everyone); `null` = unknown.
  invitationSent?: boolean | null;
  onClose: () => void;
  onConfirm: (templateId?: string) => Promise<void>;
};

const PLACEHOLDER = { value: "", label: "Select a template…" };

function AddReminderModal({ isOpen, invitationSent, onClose, onConfirm }: AddReminderModalProps) {
  const [templateId, setTemplateId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [templateOptions, setTemplateOptions] = useState<{ value: string; label: string }[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesHelperText, setTemplatesHelperText] = useState("No reminder templates available");
  const wasOpenRef = useRef(false);

  // Reset local state and (re)fetch templates when the modal opens.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setTemplateId("");
      setSubmitting(false);
      setTemplateOptions([]);
      setTemplatesLoading(true);
      setTemplatesHelperText("Loading templates…");

      let cancelled = false;
      templatesApi
        .list({ type: "reminder" })
        .then(res => {
          if (cancelled) return;
          const usable = res.templates
            .filter(t => t.id && !t.archived && !t.missing_in_sendgrid)
            .map(t => ({
              value: t.id as string,
              label: t.name ?? t.subject ?? t.sendgrid_template_id,
            }));
          setTemplateOptions(usable);
          setTemplatesHelperText(usable.length === 0 ? "No reminder templates available" : "");
        })
        .catch(() => {
          if (cancelled) return;
          setTemplateOptions([]);
          setTemplatesHelperText("Failed to load templates");
        })
        .finally(() => {
          if (cancelled) return;
          setTemplatesLoading(false);
        });

      wasOpenRef.current = isOpen;
      return () => {
        cancelled = true;
      };
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(templateId || undefined);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add reminder"
      size="small"
      backdropClick={false}
    >
      <div className={styles.modalForm}>
        {invitationSent === false && (
          <p className={styles.warningBanner}>
            No invitation has been sent to this group yet — a reminder would currently target
            everyone.
          </p>
        )}
        <Select
          label="Template"
          options={[PLACEHOLDER, ...templateOptions]}
          value={templateId}
          onChange={e => setTemplateId(e.target.value)}
          fullWidth
          disabled={templatesLoading}
          helperText={templatesHelperText || undefined}
        />
        <p className={styles.modalIntro}>
          Select a reminder template. A new reminder will be queued for the recipients who have not
          yet responded.
        </p>
        <div className={styles.modalFooter}>
          <Button variant="outlined" size="small" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? "Adding…" : "Add Reminder"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export { AddReminderModal };
